import type { CompareMode, Version } from '../types.ts';
import { DownloadMenu } from './DownloadMenu.tsx';
import { formatShortTimecode } from '../timecode.ts';

interface Props {
  version: Version | null;
  frame: number;
  frameCount: number;
  /** First frame number of the source clip (from its start timecode); 0 when the file said none. */
  startFrame: number;
  fps: number;
  playing: boolean;
  onTogglePlay: () => void;
  onStepShot: (delta: number) => void;
  loopMode: boolean;
  onToggleLoopMode: () => void;
  /** Compare needs a second version of the same shot; otherwise the picker stays hidden. */
  canCompare: boolean;
  compareMode: CompareMode;
  onCompareMode: (mode: CompareMode) => void;
  /** The other versions of this shot. */
  compareOptions: Version[];
  compareVersionId: string | null;
  onCompareVersion: (id: string | null) => void;
  drawEnabled: boolean;
  onToggleDraw: () => void;
  /** True while the host is presenting: the pen is unavailable to everyone but them. */
  drawDisabled?: boolean;
  muted: boolean;
  onToggleMuted: () => void;
  /** The project allows downloads, or we hold the admin token. */
  canDownload: boolean;
  onApprove: () => void;
  onReject: () => void;
}

/**
 * The bar under the player. Five zones: draw/compare far left · TC + prev-shot in front of play ·
 * play alone in true page-centre · next-shot + loop + quality + mute + download · verdict far right.
 * Play sits dead-centre because the outer grid puts it in its own auto column and the two 1fr
 * columns split all remaining space equally.
 */
export function ReviewControls({
  version,
  frame,
  frameCount,
  startFrame,
  fps,
  playing,
  onTogglePlay,
  onStepShot,
  loopMode,
  onToggleLoopMode,
  canCompare,
  compareMode,
  onCompareMode,
  compareOptions,
  compareVersionId,
  onCompareVersion,
  drawEnabled,
  onToggleDraw,
  drawDisabled = false,
  muted,
  onToggleMuted,
  canDownload,
  onApprove,
  onReject,
}: Props): React.JSX.Element {
  return (
    <div className="controls">
      <div className="controls__left">
        <button
          type="button"
          className={drawEnabled ? 'active' : ''}
          onClick={onToggleDraw}
          disabled={drawDisabled}
          title={drawDisabled ? 'The host is presenting' : 'Draw (d)'}
        >
          ✎ Draw
        </button>
        {canCompare ? (
          <select
            value={compareMode}
            onChange={(e) => onCompareMode(e.target.value as CompareMode)}
            title="Compare mode (w)"
          >
            <option value="off">Compare</option>
            <option value="wipe">Wipe</option>
            <option value="sidebyside">Side by side</option>
            <option value="difference">Difference</option>
          </select>
        ) : null}
        {compareMode !== 'off' ? (
          <select
            value={compareVersionId ?? ''}
            onChange={(e) => onCompareVersion(e.target.value || null)}
            title="Compare version"
          >
            <option value="">— version —</option>
            {compareOptions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="controls__pre">
        <span className="transport__tc">
          {/* Time stays clip-relative — always from 0 — while the frame number carries the
              source numbering, so a 1001-based clip reads e.g. "00:00 · f1001". */}
          {formatShortTimecode(frame, fps)}
          <span className="muted">
            {' '}
            ·{' '}
            {String(startFrame + frame).padStart(String(Math.max(0, startFrame + frameCount - 1)).length, '0')}
          </span>
        </span>
        <button type="button" onClick={() => onStepShot(-1)} title="Previous shot ([)">
          ⏮
        </button>
      </div>

      <button type="button" className="transport__play" onClick={onTogglePlay} title="Play/Pause (Space)">
        {playing ? '❚❚' : '▶'}
      </button>

      <div className="controls__post">
        <button type="button" onClick={() => onStepShot(1)} title="Next shot (])">
          ⏭
        </button>
        <button
          type="button"
          className="transport__loop"
          onClick={onToggleLoopMode}
          title={loopMode ? 'Loop current clip' : 'Play clips in sequence'}
        >
          {loopMode ? (
            '∞'
          ) : (
            <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 6l8 6-8 6V6zm9 0l8 6-8 6V6z" fill="currentColor" />
            </svg>
          )}
        </button>

        {version?.hasAudio ? (
          // No `active` class: the icon already says which state it is in, and a filled
          // chip made a plain on/off switch look like a mode the player was stuck in.
          <button
            type="button"
            onClick={onToggleMuted}
            title={muted ? 'Sound off — click to unmute' : 'Sound on — click to mute'}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
              {muted ? (
                <path d="M16 9l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
              ) : (
                <path
                  d="M16 8.5a4 4 0 0 1 0 7M18.5 6a7 7 0 0 1 0 12"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        ) : null}

        {canDownload ? <DownloadMenu versionId={version?.id ?? null} /> : null}
      </div>

      <div className="controls__right">
        <button
          type="button"
          className={`approve${version?.status === 'approved' ? ' is-active' : ''}`}
          onClick={onApprove}
          title="Approve (a)"
        >
          ✓ {version?.status === 'approved' ? 'Approved' : 'Approve'}
        </button>
        <button
          type="button"
          className={`danger${version?.status === 'rejected' ? ' is-active' : ''}`}
          onClick={onReject}
          title="Reject (r)"
        >
          ✗ {version?.status === 'rejected' ? 'Rejected' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
